import { useEffect, useRef, useState } from "react"
import * as tus from "tus-js-client"
import {
  initLessonVideoUploadApi,
  getLessonVideoStatusApi,
  LessonVideoStatus,
} from "../api/lesson.api"
import { LessonVideoPlayer } from "./LessonVideoPlayer"
import {
  FilmIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  XMarkIcon,
} from "@heroicons/react/16/solid"

type Phase = "idle" | "uploading" | "processing" | "ready" | "error"

type Props = {
  lessonId: string
  videoId: string | null
  lessonTitle?: string
  onVideoReady: (videoId: string) => void
}

const POLL_INTERVAL_MS = 2500

/** Video finished uploading to Bunny but processing wasn't confirmed done yet — survives a page reload/close so we can resume polling instead of losing track of it. */
const pendingKey = (lessonId: string) => `gspro_pending_video_${lessonId}`

export const VideoUploadField = ({ lessonId, videoId, lessonTitle, onVideoReady }: Props) => {
  const [phase, setPhase] = useState<Phase>("idle")
  const [uploadPercent, setUploadPercent] = useState(0)
  const [processPercent, setProcessPercent] = useState(0)
  const [processStatus, setProcessStatus] = useState<LessonVideoStatus | null>(null)
  const [errorMsg, setErrorMsg] = useState("")
  const [readyVideoId, setReadyVideoId] = useState<string | null>(null)

  const uploadRef = useRef<tus.Upload | null>(null)
  const cancelledRef = useRef(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  // Resume tracking a video that finished uploading before the page was closed/reloaded.
  useEffect(() => {
    const pending = localStorage.getItem(pendingKey(lessonId))
    if (pending) {
      setPhase("processing")
      pollStatus(pending)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId])

  const pollStatus = async (targetVideoId: string) => {
    while (!cancelledRef.current) {
      try {
        const res = await getLessonVideoStatusApi(lessonId, targetVideoId)
        if (cancelledRef.current) return

        setProcessStatus(res.data.status)
        setProcessPercent(res.data.encode_progress ?? 0)

        if (res.data.is_ready) {
          localStorage.removeItem(pendingKey(lessonId))
          setPhase("ready")
          setReadyVideoId(targetVideoId)
          onVideoReady(targetVideoId)
          return
        }
        if (res.data.status === "error" || res.data.status === "upload_failed") {
          localStorage.removeItem(pendingKey(lessonId))
          setPhase("error")
          setErrorMsg("Bunny не смог обработать видео")
          return
        }
      } catch {
        setPhase("error")
        setErrorMsg("Не удалось получить статус обработки видео")
        return
      }
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS))
    }
  }

  const startUpload = async (file: File) => {
    cancelledRef.current = false
    setErrorMsg("")
    setUploadPercent(0)
    setProcessPercent(0)
    setProcessStatus(null)
    setPhase("uploading")

    try {
      const initRes = await initLessonVideoUploadApi(lessonId, lessonTitle)
      const { video_id, tus_endpoint, headers } = initRes.data

      const upload = new tus.Upload(file, {
        endpoint: tus_endpoint,
        retryDelays: [0, 3000, 5000, 10000, 20000],
        headers: headers as Record<string, string>,
        metadata: { filetype: file.type },
        onError: () => {
          if (cancelledRef.current) return
          setPhase("error")
          setErrorMsg("Не удалось загрузить файл")
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          setUploadPercent(Math.round((bytesUploaded / bytesTotal) * 100))
        },
        onSuccess: () => {
          if (cancelledRef.current) return
          localStorage.setItem(pendingKey(lessonId), video_id)
          setPhase("processing")
          pollStatus(video_id)
        },
      })

      uploadRef.current = upload
      upload.start()
    } catch {
      setPhase("error")
      setErrorMsg("Не удалось начать загрузку")
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (file) startUpload(file)
  }

  const handleCancel = () => {
    cancelledRef.current = true
    uploadRef.current?.abort()
    localStorage.removeItem(pendingKey(lessonId))
    setPhase("idle")
  }

  const openPicker = () => inputRef.current?.click()

  return (
    <div>
      <input ref={inputRef} type="file" accept="video/*" onChange={handleFileChange} className="hidden" />

      {phase === "idle" && !videoId && (
        <button
          type="button"
          onClick={openPicker}
          className="w-full flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-200 rounded-xl px-4 py-8 text-sm text-gray-500 hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
        >
          <FilmIcon className="w-6 h-6 text-gray-300" />
          Загрузить видео
        </button>
      )}

      {phase === "idle" && videoId && (
        <div className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3.5 py-2.5">
          <div className="flex items-center gap-2 min-w-0 text-sm text-gray-700">
            <CheckCircleIcon className="w-4 h-4 text-emerald-500 flex-shrink-0" />
            <span className="truncate">Видео загружено</span>
            <span className="font-mono text-xs text-gray-400 truncate">{videoId}</span>
          </div>
          <button
            type="button"
            onClick={openPicker}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 flex-shrink-0"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
            Заменить
          </button>
        </div>
      )}

      {phase === "uploading" && (
        <div className="border border-gray-200 rounded-lg px-3.5 py-3 space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Загрузка: {uploadPercent}%</span>
            <button type="button" onClick={handleCancel} className="text-gray-400 hover:text-red-500">
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${uploadPercent}%` }} />
          </div>
        </div>
      )}

      {phase === "processing" && (
        <div className="border border-gray-200 rounded-lg px-3.5 py-3 space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <span>Обработка видео: {processPercent}%</span>
            <span className="text-gray-400">{processStatus}</span>
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-violet-500 rounded-full transition-all" style={{ width: `${processPercent}%` }} />
          </div>
        </div>
      )}

      {phase === "ready" && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3 border border-emerald-200 bg-emerald-50/50 rounded-lg px-3.5 py-2.5">
            <div className="flex items-center gap-2 text-sm text-emerald-700">
              <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
              Видео обработано и готово
            </div>
            <button
              type="button"
              onClick={openPicker}
              className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 flex-shrink-0"
            >
              <ArrowPathIcon className="w-3.5 h-3.5" />
              Заменить
            </button>
          </div>
          {readyVideoId && <LessonVideoPlayer lessonId={lessonId} refreshKey={readyVideoId} />}
        </div>
      )}

      {phase === "error" && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-red-600 border border-red-200 bg-red-50 rounded-lg px-3.5 py-2.5">
            <ExclamationTriangleIcon className="w-4 h-4 flex-shrink-0" />
            {errorMsg}
          </div>
          <button
            type="button"
            onClick={openPicker}
            className="text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            Попробовать снова
          </button>
        </div>
      )}
    </div>
  )
}
